// Vocabulary for uniform ITEMS (UniformItem.gender) — M/F/U. Do not use this
// for anything that filters or displays an employee's own gender; the API
// stores employee gender as full lowercase words (see employeeGenderOptions
// below), so these codes sent as ?gender= silently match zero employees.
export const uniformGenderOptions = [
    { value: "M", label: "Male" },
    { value: "F", label: "Female" },
    { value: "U", label: "Unisex" }
]

// Vocabulary for EMPLOYEES (CompanyUser.gender) — the values IssuanceEmployeesView
// (qs.filter(gender=...)) and UniformIssuanceFilter.gender (company_user__gender)
// actually filter on. Used by the issuance CASA/gender pickers and the issuance
// list's employee-gender filter.
export const employeeGenderOptions = [
    { value: "", label: "All" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" }
]

// Same as employeeGenderOptions but with "Other" — for the issuance list's
// filter drawer, which (unlike the picker radios above) should be able to
// show/filter every gender value CompanyUser can actually hold.
export const employeeGenderFilterOptions = [
    { value: "", label: "All" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" }
]

export const issuanceStatusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "ISSUED", label: "Issued" },
    { value: "ACCEPTED", label: "Accepted" },
    { value: "REJECTED", label: "Rejected" },
    { value: "RETURNED", label: "Returned" },
    // Terminal, and not the same as Returned: the employee's position no
    // longer uses this uniform, so it stopped counting as replacement
    // demand, but the garment is still with them until a return is recorded.
    { value: "SUPERSEDED", label: "No longer required" }
]

export const transferStatusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" }
]

export const stockTakeStatusOptions = [
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "COMPLETED", label: "Completed" }
]

export const transactionTypeOptions = [
    { value: "RECEIVING", label: "Receiving" },
    { value: "ISSUANCE", label: "Issuance" },
    { value: "RETURN", label: "Return" },
    { value: "TRANSFER_IN", label: "Transfer In" },
    { value: "TRANSFER_OUT", label: "Transfer Out" },
    { value: "ADJUSTMENT", label: "Adjustment" }
]

export const rejectionReasonOptions = [
    { value: "SIZE_TOO_SMALL", label: "Size Too Small" },
    { value: "SIZE_TOO_LARGE", label: "Size Too Large" },
    { value: "INCORRECT_UNIFORM", label: "Incorrect Uniform" },
    { value: "DAMAGED", label: "Damaged" },
    { value: "INCORRECT_GENDER", label: "Incorrect Gender" },
    { value: "OTHER", label: "Other" }
]

export const reviewStatusOptions = [
    { value: "APPROVED", label: "Approved" },
    { value: "DENIED", label: "Denied" }
]

export const activeStatusOptions = [
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" }
]

export const poStatusOptions = [
    { value: "DRAFT", label: "Draft" },
    { value: "OPEN", label: "Open" },
    { value: "PARTIALLY_RECEIVED", label: "Partially Received" },
    { value: "RECEIVED", label: "Fully Received" },
    { value: "CANCELLED", label: "Cancelled" }
]

export const statusBadgeMap = {
    PENDING: { color: "#faad14", bg: "#fffbe6", border: "#ffe58f" },
    ISSUED: { color: "#1890ff", bg: "#e6f7ff", border: "#91d5ff" },
    ACCEPTED: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" },
    REJECTED: { color: "#ff4d4f", bg: "#fff2f0", border: "#ffa39e" },
    RETURNED: { color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" },
    // Grey, like the other "closed, nothing to do" states: it is a fact
    // about the record, not something that went wrong.
    SUPERSEDED: { color: "#8c8c8c", bg: "#fafafa", border: "#d9d9d9" },
    COMPLETED: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" },
    CANCELLED: { color: "#8c8c8c", bg: "#fafafa", border: "#d9d9d9" },
    IN_PROGRESS: { color: "#faad14", bg: "#fffbe6", border: "#ffe58f" },
    NOT_ISSUED: { color: "#8c8c8c", bg: "#fafafa", border: "#d9d9d9" },
    DRAFT: { color: "#8c8c8c", bg: "#fafafa", border: "#d9d9d9" },
    OPEN: { color: "#1890ff", bg: "#e6f7ff", border: "#91d5ff" },
    PARTIALLY_RECEIVED: { color: "#fa8c16", bg: "#fff7e6", border: "#ffd591" },
    RECEIVED: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" }
}

// Renders an EMPLOYEE's gender (CompanyUser.gender, e.g. "male"/"female"/
// "other") — not a uniform item's gender code. Looked up against
// employeeGenderFilterOptions (not uniformGenderOptions) so it resolves the
// values this field actually holds instead of falling through to the raw
// string for every employee.
export const getGenderLabel = (code) => employeeGenderFilterOptions.find((g) => g.value && g.value === code)?.label || code || "—"

// The API only ever sends an issuance's raw status code ("ISSUED",
// "NOT_ISSUED"), never a display label, so every surface that shows one has
// to map it. `issuanceStatusOptions` is already that vocabulary — this reads
// it rather than restating it, and falls back to de-underscoring the code so
// a status added on the backend before it is added here still renders as
// words instead of shouting NOT_ISSUED at the user.
export const getIssuanceStatusLabel = (status) => {
    if (!status) return "—"
    const option = issuanceStatusOptions.find((o) => o.value === status)
    return option ? option.label : String(status).replace(/_/g, " ")
}

export const getStatusBadge = (status) => {
    const style = statusBadgeMap[status] || statusBadgeMap.NOT_ISSUED
    return {
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        borderRadius: "4px",
        padding: "2px 8px",
        fontSize: "12px",
        fontWeight: 500
    }
}
